import { Award, ClipboardCheck, RotateCcw, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FurtherReading } from "@/components/FurtherReading";
import { SaveSession } from "@/components/SaveSession";
import { weightLossPlan } from "@/data/nutritionCases";
import { fmt0, fmt1 } from "@/components/nutrition/NutritionParts";

const bandStyle = (percent) =>
  percent >= 80
    ? { label: "Competent", text: "text-emerald-600", ring: "border-emerald-300 bg-emerald-50", bar: "#10b981" }
    : percent >= 60
    ? { label: "Developing", text: "text-amber-600", ring: "border-amber-300 bg-amber-50", bar: "#f59e0b" }
    : { label: "Not yet competent", text: "text-red-600", ring: "border-red-300 bg-red-50", bar: "#ef4444" };

export const NutritionDebrief = ({ result, onNext, onRestart }) => {
  const { percent, breakdown, solved, caseTitle, c } = result;
  const s = bandStyle(percent);
  const mode = c.plan.portionMode;
  const proj = weightLossPlan(c);

  return (
    <div className="space-y-5 fade-up">
      <div className="bg-white border border-emerald-200 rounded-xl p-6 shadow-lg" data-testid="nutrition-debrief">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-semibold mb-5">
          <ClipboardCheck className="w-4 h-4" />
          <span>Nutrition drill debrief · {caseTitle}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Grade */}
          <div className="flex items-center gap-5">
            <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-4 ${s.ring}`} data-testid="debrief-grade">
              <Award className={`w-5 h-5 ${s.text}`} />
              <span className={`text-4xl font-bold leading-none ${s.text}`} data-testid="debrief-percent">
                {percent}
              </span>
              <span className="text-[10px] text-slate-400 font-semibold">/100</span>
            </div>
            <div>
              <div className={`text-xl font-bold ${s.text}`} data-testid="debrief-band">{s.label}</div>
              <div className="text-xs text-slate-500 mt-1">Cert IV nutrition competency</div>
            </div>
          </div>

          {/* Per-stage breakdown */}
          <div className="lg:col-span-2" data-testid="debrief-breakdown">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Stage scores</div>
            <div className="space-y-2">
              {breakdown.map((b) => {
                const pct = Math.round((b.earned / b.max) * 100);
                return (
                  <div key={b.key} data-testid={`debrief-stage-${b.key}`}>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className="text-slate-700 font-medium">{b.label}</span>
                      <span className="font-mono-clinical font-semibold text-slate-800">{b.earned}/{b.max}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444" }}
                      />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{b.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Key numbers reference */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="debrief-numbers">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">RER</div>
            <div className="font-mono-clinical text-sm font-semibold text-slate-900">{fmt0(solved.RER)} kcal</div>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Factor</div>
            <div className="font-mono-clinical text-sm font-semibold text-slate-900">×{solved.factor}</div>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">MER</div>
            <div className="font-mono-clinical text-sm font-semibold text-slate-900">{fmt0(solved.MER)} kcal</div>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Portion</div>
            <div className="font-mono-clinical text-sm font-semibold text-slate-900">
              {mode === "dry" ? `${fmt0(solved.grams)} g` : mode === "wet" ? `${fmt1(solved.cans)} cans` : `${fmt0(solved.ml)} mL`}
            </div>
          </div>
        </div>

        {proj && (
          <div className="mt-3 text-[11px] text-slate-600" data-testid="debrief-weight-projection">
            <span className="font-semibold text-[#0047AB]">Weight-loss timeline:</span>{" "}
            {fmt1(proj.current)} → {fmt1(proj.ideal)} kg at ~{proj.ratePct}%/wk ≈ {proj.weeks} weeks (~{proj.months} months).
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={onNext} data-testid="next-case-btn" className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white">
            Next case <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <Button onClick={onRestart} variant="outline" data-testid="restart-btn" className="text-slate-600">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Back to start
          </Button>
        </div>
      </div>

      <SaveSession
        simulatorId="nutrition"
        simulatorName="Nutrition Calculator Trainer"
        score={percent}
        grade={s.label}
        summary={`${caseTitle} · ${s.label}`}
      />

      <FurtherReading topic="nutrition" />
    </div>
  );
};
